import { Modal, Radio, Space } from 'antd'
import { createElement } from 'react'

import { findCustomersByPhone } from '../network/Customers'
import { hasCustomer, hasPhoneNumber } from '../constants/forms/GiftCard'
import { normalizePhoneDigits } from './phoneNumber'

const isSamePhone = (a, b) => {
    const digitsA = normalizePhoneDigits(a)
    const digitsB = normalizePhoneDigits(b)

    if (!digitsA || !digitsB) {
        return false
    }

    return digitsA === digitsB
}

export const shouldLookupPhone = (values, dismissedPhone) => {
    const phone =
        values?.phoneNumber != null ? String(values.phoneNumber) : ''

    if (!hasPhoneNumber(phone)) {
        return false
    }

    if (hasCustomer(values?.customer)) {
        return false
    }

    if (dismissedPhone && isSamePhone(dismissedPhone, phone)) {
        return false
    }

    return true
}

const linkCustomerOnForm = (form, customer) => {
    form.setFieldsValue({
        customer: {
            value: customer.id,
            label: customer.name || customer.id,
        },
        phoneNumber: undefined,
    })
}

const promptSingleCustomer = ({ customer, form, t, normalizedDigits, setDismissedPhone }) =>
    new Promise((resolve) => {
        Modal.confirm({
            title: t('GIFT_CARDS_PHONE_ALREADY_USED_TITLE'),
            content: t('GIFT_CARDS_PHONE_ALREADY_USED_MESSAGE', {
                name: customer.name,
            }),
            okText: t('GIFT_CARDS_PHONE_LINK_CUSTOMER'),
            cancelText: t('CANCEL'),
            onOk: () => {
                linkCustomerOnForm(form, customer)
                resolve(true)
            },
            onCancel: () => {
                setDismissedPhone(normalizedDigits)
                resolve(true)
            },
        })
    })

const promptMultipleCustomers = ({
    customers,
    form,
    t,
    normalizedDigits,
    setDismissedPhone,
}) =>
    new Promise((resolve) => {
        let selectedId = customers[0]?.id

        Modal.confirm({
            title: t('GIFT_CARDS_PHONE_MULTIPLE_CUSTOMERS_TITLE'),
            content: createElement(
                'div',
                null,
                createElement(
                    'p',
                    { style: { marginBottom: 12 } },
                    t('GIFT_CARDS_PHONE_MULTIPLE_CUSTOMERS_MESSAGE', {
                        count: customers.length,
                    })
                ),
                createElement(
                    Radio.Group,
                    {
                        defaultValue: selectedId,
                        onChange: (e) => {
                            selectedId = e.target.value
                        },
                    },
                    createElement(
                        Space,
                        { direction: 'vertical' },
                        ...customers.map((customer) =>
                            createElement(
                                Radio,
                                { key: customer.id, value: customer.id },
                                customer.name || customer.id
                            )
                        )
                    )
                )
            ),
            okText: t('GIFT_CARDS_PHONE_LINK_CUSTOMER'),
            cancelText: t('CANCEL'),
            onOk: () => {
                const customer = customers.find((c) => c.id === selectedId)

                if (!customer) {
                    setDismissedPhone(normalizedDigits)
                    resolve(true)
                    return
                }

                linkCustomerOnForm(form, customer)
                resolve(true)
            },
            onCancel: () => {
                setDismissedPhone(normalizedDigits)
                resolve(true)
            },
        })
    })

export const promptLinkCustomerByPhone = async ({
    phone,
    token,
    form,
    t,
    dismissedPhone,
    setDismissedPhone,
}) => {
    const normalizedDigits = normalizePhoneDigits(phone)

    if (!normalizedDigits) {
        return false
    }

    if (dismissedPhone && isSamePhone(dismissedPhone, phone)) {
        return false
    }

    const customers = await findCustomersByPhone(phone, token)

    if (!customers.length) {
        return false
    }

    if (customers.length === 1) {
        return promptSingleCustomer({
            customer: customers[0],
            form,
            t,
            normalizedDigits,
            setDismissedPhone,
        })
    }

    return promptMultipleCustomers({
        customers,
        form,
        t,
        normalizedDigits,
        setDismissedPhone,
    })
}

export const attachGiftCardPhoneBlurToTemplate = (template, onBlur) => ({
    ...template,
    sections: template.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
            if (field.key?.[0] !== 'phoneNumber') {
                return field
            }

            return {
                ...field,
                onBlur,
            }
        }),
    })),
})
